/**
 * CitationPanel
 *
 * Displays citation details in a slide-in panel from the right
 * (positioned to the left of the chat sidebar).
 */

import React from 'react';
import type { AgentCitation } from '../../../api/services/agent';

interface CitationPanelProps {
  citation: AgentCitation | null;
  onClose: () => void;
}

export const CitationPanel: React.FC<CitationPanelProps> = ({ citation, onClose }) => {
  if (!citation) {
    return null;
  }

  return (
    <div
      className="w-80 border-l border-neutral-200 bg-neutral-50 flex flex-col animate-in slide-in-from-right-2 duration-200"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-white">
        <div className="flex items-center gap-2 min-w-0">
          <svg
            className="w-4 h-4 text-blue-600 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span className="text-sm font-medium text-neutral-800 truncate">
            {citation.path.split('/').pop()}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded transition-colors shrink-0"
          aria-label="Close citation"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Source path */}
        <div>
          <p className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1.5">Source</p>
          <p className="text-xs text-neutral-800 font-mono bg-white px-3 py-2 rounded border border-neutral-200 break-all">
            {citation.path}
          </p>
        </div>

        {/* Line numbers */}
        {(citation.start_line || citation.end_line) && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1.5">Lines</p>
            <p className="text-sm text-neutral-700">
              {citation.start_line}
              {citation.end_line && citation.end_line !== citation.start_line && (
                <span> – {citation.end_line}</span>
              )}
            </p>
          </div>
        )}

        {/* Content/Quote */}
        {citation.text && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-neutral-500 mb-1.5">Content</p>
            <div className="text-sm text-neutral-800 bg-white p-4 rounded-lg font-mono whitespace-pre-wrap border border-neutral-200 leading-relaxed">
              {citation.text}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CitationPanel;

